import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {loadDesignGallery} from '@Libraries/poster-library';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import styles from './design-gallery.module.scss';
import type {DesignSelection} from '@Components/social-media/social-media.types';

interface DesignGalleryProps {
  onDesignsSelected: (designs: DesignSelection[]) => void;
  disableCreateFromScratchCTA?: boolean;
  height?: string;
  startingPageURI?: GalleryPageURI;
}

export function DesignGallery({
  onDesignsSelected,
  disableCreateFromScratchCTA = true,
  height = '80vh',
  startingPageURI = GalleryThemeURI.DEFAULT,
}: DesignGalleryProps): ReactElement<any> | null {
  const [isGalleryLoaded, setIsGalleryLoaded] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const galleryDesignClickedEvent = 'gallery-design-clicked';
  const loadDesignGalleryInElement = async (): Promise<void> => {
    await loadDesignGallery('.js-design-gallery-embedded-content', '.js-embedded-gallery-container', disableCreateFromScratchCTA, startingPageURI);
  };

  const onGalleryDesignClicked = (event: CustomEvent): void => {
    const posterHashedId = event.detail.id as string | undefined;
    const pageHashedId = event.detail.idPage as string | undefined;

    if (!posterHashedId || !pageHashedId) {
      return;
    }

    const designSelection: DesignSelection = {
      posterHashedId: posterHashedId,
      pageHashedId: pageHashedId,
    };
    onDesignsSelected([designSelection]);
  };

  useEffect(() => {
    if (!isGalleryLoaded) {
      void loadDesignGalleryInElement().then((): void => {
        setIsGalleryLoaded(true);
      });
    }

    window.addEventListener(galleryDesignClickedEvent, onGalleryDesignClicked as EventListener);

    return (): void => {
      window.removeEventListener(galleryDesignClickedEvent, onGalleryDesignClicked as EventListener);
    };
  }, []);

  return (
    <div ref={parentRef}>
      <div className={`js-embedded-gallery-container gallery-content-container ${styles.galleryContentContainer} ${styles.galleryScrollStyles}`} style={{height}}>
        <div className={`content  ${styles.mainContentContainer}`}>
          <div className={`js-loader-container ${styles.loaderBackground}`} />
          <div className={`js-design-gallery-embedded-content ${styles.galleryContent}`} />
        </div>
      </div>
    </div>
  );
}
